// src/Components/Navbar/index.jsx
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import MeetUsWrapper from "../MeetUsWrapper";
import Button from "Atoms/Buttons/Button";
import Image from "Components/Global/Image";
import FeaturesDropdown from "./FeaturesDropdown";
import ResourcesDropdown from "./ResourcesDropdown";

import MovEllipseBig from "Utils/Assets/Icons/MovEllipseBig";
import RedEllipseBig from "Utils/Assets/Icons/RedEllipseBig";
import { AiOutlineClose } from "react-icons/ai";
import { FiMenu } from "react-icons/fi";
import arrowDown from "Utils/Assets/Icons/arrow_down";

import { navbarConfig } from "./config";
import meetusvrlogo from "../../Utils/Assets/Images/meetusvrlogo.png";
import styles from "./style.module.scss";

const makeDropdownVariants = (isLargeScreen) => ({
    initial: { opacity: 0, y: -8, ...(isLargeScreen && { x: "-50%" }) },
    animate: { opacity: 1, y: 0, ...(isLargeScreen && { x: "-50%" }) },
    exit: { opacity: 0, y: -8, ...(isLargeScreen && { x: "-50%" }) },
    transition: { duration: 0.2, ease: "easeOut" },
});

// Helper functions for active path
const normalize = (p) => (p ? p.replace(/\/+$/, "") : "");
const useActivePath = () => {
    const { pathname } = useLocation();

    const isPathActive = (path) => {
        if (!path) return false;
        const normPath = normalize(path);
        const normLocation = normalize(pathname);
        if (normPath === "") return normLocation === "";
        if (normPath === "/") return normLocation === "/";
        return (
            normLocation === normPath || normLocation.startsWith(normPath + "/")
        );
    };

    const isPageActive = (page) => {
        if (!page) return false;
        if (page.path && isPathActive(page.path)) return true;
        if (page.hasDropDown && Array.isArray(page.children)) {
            return page.children.some((c) => isPathActive(c.path));
        }
        return false;
    };

    return { isPathActive, isPageActive };
};

const Navbar = () => {
    const { links } = navbarConfig;
    const navRef = useRef(null);
    const firstDropDownRef = useRef(null);
    const secondDropDownRef = useRef(null);
    const { pathname } = useLocation();

    const [openNav, setOpenNav] = useState(false);
    const [openDropDown, setOpenDropDown] = useState(null);
    const [isLargeScreen, setIsLargeScreen] = useState(false);

    const { isPathActive, isPageActive } = useActivePath();

    const handleTriggerNav = useCallback(() => {
        setOpenNav((prev) => !prev);
    }, []);

    const handleDropDown = useCallback((id) => {
        setOpenDropDown((prev) => (prev === id ? null : id));
    }, []);

    const dropdownRef = (hasDropDown, id) => {
        if (!hasDropDown) return null;
        return id === 1 ? firstDropDownRef : secondDropDownRef;
    };

    // Close dropdown on route change
    useEffect(() => {
        setOpenDropDown(null);
    }, [pathname]);

    // Screen resize handler
    useEffect(() => {
        const handleResize = () => setIsLargeScreen(window.innerWidth > 1200);
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Scroll margin top effect
    useEffect(() => {
        if (!navRef.current) return;
        const handleScroll = () => {
            navRef.current.style.marginTop =
                window.scrollY === 0 ? "20px" : "0px";
        };
        handleScroll();
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const dropdownVariants = makeDropdownVariants(isLargeScreen);

    // Dropdown content
    const dropdownContent = ({ page }) => {
        if (!page.children) return null;

        if (page.id === 1) {
            return <FeaturesDropdown pages={page.children} />;
        }
        if (page.id === 2) {
            return (
                <ResourcesDropdown
                    pages={page.children}
                    additionalContent={page.additionalContent}
                />
            );
        }
        return (
            <ul className={styles.dropDown}>
                {page.children.map((child, index) => (
                    <li key={index}>
                        <Link
                            to={child.path}
                            className={`${styles.dropdownLink} ${
                                isPathActive(child.path) ? styles.active : ""
                            }`}
                        >
                            <span className={styles.dot}></span>
                            <p className={styles.text}>{child.title}</p>
                        </Link>
                    </li>
                ))}
            </ul>
        );
    };

    return (
        <MeetUsWrapper className={styles.meetUsWrapper}>
            <nav
                className={`${styles.navbarContainer} ${openNav ? styles.openNav : ""}`}
                ref={navRef}
            >
                {/* Logo + Menu */}
                <section className={styles.navbarSubContainer}>
                    <div className={styles.navbarLogo}>
                        <Link
                            to="/"
                            onClick={() => openNav && handleTriggerNav()}
                        >
                            <Image
                                src={meetusvrlogo}
                                alt="meetUs vr"
                                width={129}
                                height={28}
                            />
                        </Link>
                        <div
                            className={styles.menuIcon}
                            onClick={handleTriggerNav}
                        >
                            {openNav ? <AiOutlineClose /> : <FiMenu />}
                        </div>
                    </div>
                    <div className={styles.verticalLine} />

                    {/* Links */}
                    <ul className={styles.navbarLinks}>
                        {links.map((page) => {
                            const pageActive = isPageActive(page);
                            return (
                                <li
                                    key={page.id}
                                    ref={dropdownRef(page.hasDropDown, page.id)}
                                    onClick={() =>
                                        page.hasDropDown &&
                                        handleDropDown(page.id)
                                    }
                                >
                                    {page.hasDropDown ? (
                                        <span
                                            className={
                                                pageActive ||
                                                openDropDown === page.id
                                                    ? styles.activeDropdown
                                                    : ""
                                            }
                                        >
                                            {page.name} {arrowDown}
                                        </span>
                                    ) : (
                                        <Link
                                            to={page.path}
                                            className={`${styles.pageLink} ${
                                                isPathActive(page.path)
                                                    ? styles.active
                                                    : ""
                                            }`}
                                        >
                                            {page.name}
                                        </Link>
                                    )}

                                    {/* Dropdown animation */}
                                    <AnimatePresence>
                                        {openDropDown === page.id &&
                                            page.hasDropDown && (
                                                <motion.div
                                                    key="dropdown"
                                                    className={styles.dropdownMotionWrapper}
                                                    initial={dropdownVariants.initial}
                                                    animate={dropdownVariants.animate}
                                                    exit={dropdownVariants.exit}
                                                    transition={dropdownVariants.transition}
                                                >
                                                    {dropdownContent({ page })}
                                                </motion.div>
                                            )}
                                    </AnimatePresence>
                                </li>
                            );
                        })}
                    </ul>
                </section>

                {/* Buttons */}
                <section className={styles.buttonsContainer}>
                    <Button
                        className={styles.downloadButton}
                        name="downloadAppButton"
                    >
                        Download App
                    </Button>
                </section>

                {/* Background ellipses */}
                <div className={styles.ellipseBig}>
                    <div>
                        {MovEllipseBig}
                        {RedEllipseBig}
                    </div>
                </div>
            </nav>
        </MeetUsWrapper>
    );
};

export default Navbar;
